import React, { useState } from "react";
import { BellRing, Check, Gift, Mail, Send, ShieldCheck, Sparkles, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { Reveal } from "./Reveal";

const PERKS = [
  { icon: Ticket, text: "Kod −5% na dowolny zakup w DigiLib.store — od razu na Twój e-mail" },
  { icon: BellRing, text: "Nowości, promocje i premiery aplikacji raz w tygodniu" },
  { icon: ShieldCheck, text: "Zero spamu — zrezygnujesz w każdej chwili" },
];

// Duża sekcja zapisu do newslettera: za zapis klient dostaje jednorazowy kod rabatowy -5% e-mailem
export function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@") || !email.split("@")[1]?.includes(".")) { toast.error("Podaj poprawny adres e-mail"); return; }
    if (!consent) { toast.error("Zaznacz zgodę na otrzymywanie newslettera"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      setDone({ email, message: data.message, sent: data.discount_sent !== false });
      setEmail("");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się zapisać. Spróbuj ponownie.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="newsletter" className="mx-auto max-w-7xl scroll-mt-24 px-4 pb-20 sm:px-6" data-testid="newsletter-section">
      <Reveal>
        <div className="rounded-[2rem] bg-gradient-to-br from-cyan-400/60 via-fuchsia-400/40 to-teal-300/60 p-[2px] shadow-[0_30px_90px_-30px_rgba(34,211,238,0.5)]">
          <div className="relative overflow-hidden rounded-[calc(2rem-2px)] bg-slate-950">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_80%_at_0%_0%,rgba(34,211,238,0.22),transparent_60%),radial-gradient(ellipse_55%_70%_at_100%_100%,rgba(217,70,239,0.20),transparent_60%)]" />
            <div className="relative grid items-center gap-10 p-6 sm:p-10 lg:grid-cols-[1.15fr_0.85fr] lg:p-14">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-cyan-300">
                  <Gift className="h-3.5 w-3.5" /> Prezent na start
                </span>
                <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                  Zapisz się i odbierz <span className="gradient-text">−5%</span> na dowolny zakup
                </h2>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300">
                  Dołącz do newslettera DigiLib.store. Po zapisie wyślemy Ci na e-mail <b className="text-white">jednorazowy kod rabatowy −5%</b> — wpiszesz go w koszyku i zapłacisz mniej za ebooki, audiobooki, aplikacje i gry.
                </p>
                <ul className="mt-6 space-y-3">
                  {PERKS.map((p) => (
                    <li key={p.text} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cyan-400/15 text-cyan-300"><p.icon className="h-4 w-4" /></span>
                      <span className="pt-0.5">{p.text}</span>
                    </li>
                  ))}
                </ul>

                {done ? (
                  <div className="mt-8 flex items-start gap-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5" data-testid="newsletter-success">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300"><Check className="h-6 w-6" /></span>
                    <div>
                      <div className="font-display text-lg font-bold text-emerald-100">{done.sent ? "Sprawdź skrzynkę!" : "Zapisano!"}</div>
                      <p className="mt-1 text-sm leading-relaxed text-emerald-200/90">{done.message}</p>
                      <p className="mt-2 text-xs text-emerald-300/70">Adres: {done.email}. Nie widzisz wiadomości? Zajrzyj do folderu Spam.</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-8" data-testid="newsletter-big-form">
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <div className="relative flex-1">
                        <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
                        <input
                          data-testid="newsletter-big-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Twój adres e-mail"
                          autoComplete="email"
                          className="w-full rounded-2xl border border-slate-700 bg-slate-900/80 py-3.5 pl-12 pr-4 text-base text-slate-100 placeholder-slate-500 outline-none transition-colors focus:border-cyan-400"
                        />
                      </div>
                      <button
                        data-testid="newsletter-big-submit"
                        disabled={sending}
                        className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 to-teal-400 px-7 py-3.5 font-display text-base font-extrabold text-slate-950 shadow-lg shadow-cyan-500/30 transition-transform hover:scale-[1.03] active:scale-95 disabled:opacity-60"
                      >
                        {sending ? "Wysyłanie..." : <>Odbierz kod −5% <Send className="h-4 w-4" /></>}
                      </button>
                    </div>
                    <label className="mt-4 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-slate-400">
                      <input
                        data-testid="newsletter-big-consent"
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-cyan-400"
                      />
                      <span>Wyrażam zgodę na otrzymywanie na podany adres e-mail informacji handlowych i newslettera DigiLib.store. Zgodę mogę wycofać w każdej chwili. Administrator danych: DigiLib.store.</span>
                    </label>
                  </form>
                )}
              </div>

              {/* wizualizacja: kupon */}
              <div className="relative mx-auto w-full max-w-sm" aria-hidden="true">
                <div className="absolute -inset-6 rounded-full bg-gradient-to-br from-cyan-500/25 via-fuchsia-500/20 to-teal-400/25 blur-3xl" />
                <div className="relative rotate-[-3deg] rounded-3xl border border-cyan-300/30 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950 p-6 shadow-2xl shadow-cyan-500/20">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-cyan-300"><Sparkles className="h-4 w-4" /> DigiLib.store</span>
                    <span className="rounded-full bg-amber-400 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-950">Kupon</span>
                  </div>
                  <div className="mt-5 flex items-end gap-2">
                    <span className="gradient-text font-display text-7xl font-extrabold leading-none">−5%</span>
                    <span className="pb-2 text-sm font-semibold text-slate-300">na dowolny zakup</span>
                  </div>
                  <div className="my-5 border-t-2 border-dashed border-cyan-300/30" />
                  <div className="rounded-xl border border-dashed border-cyan-300/40 bg-cyan-400/5 px-4 py-3 text-center">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Twój kod</div>
                    <div className="mt-1 font-mono text-xl font-extrabold tracking-[0.25em] text-white">DIGI5-••••••</div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-xs text-slate-400"><Mail className="h-4 w-4 text-cyan-300" /> Przyjdzie na Twój e-mail zaraz po zapisie</div>
                  <span className="absolute -left-3 top-[52%] h-6 w-6 rounded-full bg-slate-950" />
                  <span className="absolute -right-3 top-[52%] h-6 w-6 rounded-full bg-slate-950" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
